<template>
  <div ref="root" class="copy-synopsis-select">
    <button
      id="contest-entry-copy-synopsis"
      type="button"
      class="trigger"
      aria-haspopup="menu"
      :aria-expanded="isOpen"
      aria-controls="contest-entry-copy-synopsis-menu"
      @click="toggle"
    >
      <span class="trigger-label">{{ t("contestEntryForm.step2.copySynopsis.placeholder") }}</span>
      <IconChevronDown class="trigger-chevron" />
    </button>
    <div
      v-show="isOpen"
      id="contest-entry-copy-synopsis-menu"
      class="menu"
      role="menu"
      :aria-label="t('contestEntryForm.step2.copySynopsis.placeholder')"
    >
      <button
        v-for="option in options"
        :key="option.value"
        type="button"
        class="menu-item"
        role="menuitem"
        @click="onSelect(option.value)"
      >
        {{ option.label }}
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, defineEmits, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n-bridge";
import IconChevronDown from "~icons/figma/ChevronDown";

export type ContestEntryCopySynopsisSource = "fromNovel" | "fromTheme";

const emit = defineEmits<{
  (e: "select", source: ContestEntryCopySynopsisSource): void;
}>();

const { t } = useI18n();
const root = ref<HTMLElement | null>(null);
const isOpen = ref(false);

const options = computed(() => [
  {
    value: "fromNovel" as const,
    label: t("contestEntryForm.step2.copySynopsis.fromNovel"),
  },
  {
    value: "fromTheme" as const,
    label: t("contestEntryForm.step2.copySynopsis.fromTheme"),
  },
]);

function toggle() {
  isOpen.value = !isOpen.value;
}

function close() {
  isOpen.value = false;
}

function onSelect(source: ContestEntryCopySynopsisSource) {
  close();
  emit("select", source);
}

function onDocumentPointerDown(event: PointerEvent) {
  if (!isOpen.value) return;
  const target = event.target as Node | null;
  if (target && root.value?.contains(target)) return;
  close();
}

function onDocumentKeydown(event: KeyboardEvent) {
  if (!isOpen.value) return;
  if (event.key === "Escape") close();
}

onMounted(() => {
  document.addEventListener("pointerdown", onDocumentPointerDown);
  document.addEventListener("keydown", onDocumentKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener("pointerdown", onDocumentPointerDown);
  document.removeEventListener("keydown", onDocumentKeydown);
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.copy-synopsis-select {
  position: relative;
  width: max-content;
  max-width: 276px;
}

.trigger {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  min-height: 48px;
  padding: 12px;
  border: none;
  border-radius: var(--radius-md);
  background-color: var(--color-surface-base);
  color: var(--color-text-normal);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-default);
  text-align: left;
  cursor: pointer;

  @include hover-overlay;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }
}

.trigger-label {
  white-space: nowrap;
}

.trigger-chevron {
  flex-shrink: 0;
  font-size: var(--font-size-md);
  color: var(--color-text-normal);
}

.menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 10;
  width: 100%;
  padding: 8px 0;
  border-radius: var(--radius-md);
  background-color: var(--color-background-normal);
  box-shadow: var(--shadow-md);
}

.menu-item {
  position: relative;
  display: block;
  width: 100%;
  padding: 16px 24px;
  border: none;
  background: transparent;
  color: var(--color-text-normal);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-default);
  text-align: left;
  cursor: pointer;

  @include hover-overlay;

  &:focus {
    outline: none;
    box-shadow: inset 0 0 0 2px var(--color-focus-ring);
  }
}

@include mq("sp") {
  .copy-synopsis-select {
    width: 100%;
    max-width: none;
  }
}
</style>
